import { Box, FormControlLabel, Switch, Typography } from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import { scaleLinear } from "d3-scale";
import { memo, useMemo, useState } from "react";
import useFetchGraphData from "../../../hooks/useFetchGraphData";
import DepthHeatmapChart from "./DepthHeatmapChart";

// Temperature color scale (same as parameterid 8 in DepthHeatmapChartContainer)
const TEMPERATURE_SCALE = {
  breaks: [0, 8, 13, 16, 19, 21, 22, 23, 24, 26.2],
  colors: [
    "#4e65ac",
    "#6869af",
    "#5ca7ba",
    "#7ecba7",
    "#addeac",
    "#f6fbb5",
    "#ff9b93",
    "#e6665c",
    "#c43a2a",
    "#8b1a0e",
  ],
  rationale:
    "Regulation 38 Aquatic Life 2 standards specify chronic (<26.2°C) and acute (<29.3°C) thresholds. Elevated temperatures can reduce oxygen levels and stress aquatic species.",
};

function TemperatureHeatmap15MinChartContainer({
  selectedDate,
  height = 400,
  showLegend = true,
  title = null,
}) {
  const [showValues, setShowValues] = useState(false);
  const {
    data = [],
    isLoading,
    isError,
  } = useFetchGraphData(selectedDate ? { rdate: selectedDate } : null, [
    "thermistor-data/15-min",
  ]);

  const colorScale = useMemo(
    () =>
      scaleLinear()
        .domain(TEMPERATURE_SCALE.breaks)
        .range(TEMPERATURE_SCALE.colors),
    []
  );

  const { matrix, timestamps, unit } = useMemo(() => {
    if (!data.length) return { matrix: [], timestamps: [], unit: "°C" };

    const depths = [...new Set(data.map((d) => d.depth_value))].sort(
      (a, b) => a - b
    );
    const uniqueTimestamps = [...new Set(data.map((d) => d.rtimestamp))].sort();

    const matrix = depths.map((depth) => ({
      id: String(depth),
      data: uniqueTimestamps.map((timestamp) => {
        const point = data.find(
          (r) => r.depth_value === depth && r.rtimestamp === timestamp
        );
        return { x: timestamp, y: point?.result_value ?? null };
      }),
    }));

    return {
      matrix,
      timestamps: uniqueTimestamps,
      unit: data[0]?.unit_name || "°C",
    };
  }, [data]);

  if (!selectedDate) {
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="body1" color="textSecondary">
          Click on a cell in the daily chart above to view 15-minute data.
        </Typography>
      </Box>
    );
  }

  if (isLoading)
    return (
      <Skeleton variant="rect" height={height} width="100%" animation="wave" />
    );

  if (isError || !matrix.length)
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        No 15-minute data available for {selectedDate}
      </Box>
    );

  return (
    <>
      {title && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" component="h2">
            {title}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showValues}
                onChange={(e) => setShowValues(e.target.checked)}
                color="primary"
              />
            }
            label="Show values in cells"
          />
        </Box>
      )}
      {!title && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showValues}
                onChange={(e) => setShowValues(e.target.checked)}
                color="primary"
              />
            }
            label="Show values in cells"
          />
        </Box>
      )}

      <DepthHeatmapChart
        matrix={matrix}
        dates={timestamps}
        colorScale={colorScale}
        breaks={showLegend ? TEMPERATURE_SCALE.breaks : []}
        colors={showLegend ? TEMPERATURE_SCALE.colors : []}
        unit={unit}
        height={height}
        showValues={showValues}
        xAxisFormat="time"
      />

      <Box mt={2} width="100%">
        <Alert severity="info">{TEMPERATURE_SCALE.rationale}</Alert>
      </Box>
    </>
  );
}

export default memo(TemperatureHeatmap15MinChartContainer);
